/**
 * UIManager - Handles user interface elements and interactions
 */
export class UIManager {
    constructor() {
        this.screens = {};
        this.activeScreen = null;
        this.uiElements = {};
        this.eventListeners = {};
        
        // References to DOM elements
        this.overlay = null;
        this.loadingScreen = null;
        this.loginScreen = null;
        this.characterSelectScreen = null;
        this.gameUI = null;
        
        // Event callbacks
        this.callbacks = {
            onLogin: null,
            onCharacterSelect: null,
            onInventoryChange: null
        };
        
        // Selected character class
        this.selectedCharacterClass = null;
    }
    
    /**
     * Initialize the UI manager
     */
    init() {
        console.log('Initializing UIManager...');
        
        // Get references to UI DOM elements
        this.overlay = document.getElementById('ui-overlay');
        this.loadingScreen = document.getElementById('loading-screen');
        this.loginScreen = document.getElementById('login-screen');
        
        // Create UI screens (references to DOM elements in the index.html)
        this.screens = {
            loading: this.loadingScreen,
            login: this.loginScreen,
            game: this.overlay
        };
        
        // Initialize uiElements if not already defined
        this.uiElements = this.uiElements || {};
        
        // Create character selection screen
        this.createCharacterSelectScreen();
        
        // Create in-game UI elements
        this.createGameUI();
        
        // Add loading bar to login screen (only if needed)
        this.addLoadingBarToLoginScreen();
        
        // Add pulse animation CSS
        this.addPulseAnimationStyle();
        
        // Add required UI styles
        this.addUIStyles();
        
        // Set initial active screen to login instead of loading
        this.activeScreen = 'login';
        
        // Setup mobile optimization
        this.setupMobileOptimization();
        
        // Make the login screen visible
        if (this.loginScreen) {
            this.loginScreen.classList.remove('hidden');
        }
        
        // Hide the loading screen
        if (this.loadingScreen) {
            this.loadingScreen.classList.add('hidden');
        }
        
        console.log('UIManager initialized');
        
        return true;
    }
    
    /**
     * Add loading bar to login screen
     */
    addLoadingBarToLoginScreen() {
        const loginContainer = document.querySelector('.login-container');
        if (!loginContainer) {
            console.error('Login container not found');
            return;
        }
        
        // Check if a loading bar already exists in the login container
        if (loginContainer.querySelector('.login-loading-container')) {
            console.log('Loading bar already exists in login container, skipping creation');
            return;
        }
        
        // Create loading bar container
        const loadingContainer = document.createElement('div');
        loadingContainer.className = 'login-loading-container';
        
        // Create loading bar
        const loadingBar = document.createElement('div');
        loadingBar.className = 'login-loading-bar';
        
        // Create loading progress
        const loadingProgress = document.createElement('div');
        loadingProgress.className = 'login-loading-progress';
        
        // Create loading text
        const loadingText = document.createElement('p');
        loadingText.className = 'login-loading-text';
        loadingText.textContent = 'Loading game assets...';
        
        // Assemble elements
        loadingBar.appendChild(loadingProgress);
        loadingContainer.appendChild(loadingBar);
        loadingContainer.appendChild(loadingText);
        
        // Add to login container
        loginContainer.appendChild(loadingContainer);
        
        // Disable the login button until loading is complete
        const enterGameBtn = document.getElementById('enter-game-btn');
        if (enterGameBtn) {
            enterGameBtn.disabled = true;
            enterGameBtn.textContent = "Loading...";
        }
    }
    
    /**
     * Create character selection screen
     */
    createCharacterSelectScreen() {
        // Create character selection container
        this.characterSelectScreen = document.createElement('div');
        this.characterSelectScreen.id = 'character-select-screen';
        this.characterSelectScreen.className = 'screen hidden';
        
        // Create header
        const header = document.createElement('h1');
        header.textContent = 'Choose Your Character';
        this.characterSelectScreen.appendChild(header);
        
        // Create character options container
        const characterOptions = document.createElement('div');
        characterOptions.className = 'character-options';
        
        // Barbarian option
        const barbarianOption = document.createElement('div');
        barbarianOption.className = 'character-option';
        barbarianOption.innerHTML = `
            <div class="character-preview barbarianPreview"></div>
            <h2>Barbarian</h2>
            <ul>
                <li>Health: 100</li>
                <li>Speed: Faster</li>
                <li>Starting Weapon: Axe</li>
                <li>Special: High melee damage</li>
            </ul>
            <button class="select-character-btn" data-character="barbarian">Select Barbarian</button>
        `;
        
        // MAGE option
        const mageOption = document.createElement('div');
        mageOption.className = 'character-option';
        mageOption.innerHTML = `
            <div class="character-preview magePreview"></div>
            <h2>Mage</h2>
            <ul>
                <li>Health: 100</li>
                <li>Speed: Slower</li>
                <li>Starting Weapon: Staff</li>
                <li>Special: Long range attacks</li>
            </ul>
            <button class="select-character-btn" data-character="mage">Select Mage</button>
        `;
        
        // KNIGHT option
        const knightOption = document.createElement('div');
        knightOption.className = 'character-option';
        knightOption.innerHTML = `
            <div class="character-preview knightPreview"></div>
            <h2>Knight</h2>
            <ul>
                <li>Health: 100</li>
                <li>Speed: Faster</li>
                <li>Starting Weapon: Sword</li>
                <li>Special: Close range attacks</li>
            </ul>
            <button class="select-character-btn" data-character="knight">Select Knight</button>
        `;
        
        // ROGUE option
        const rogueOption = document.createElement('div');
        rogueOption.className = 'character-option';
        rogueOption.innerHTML = `
            <div class="character-preview roguePreview"></div>
            <h2>Rogue</h2>
            <ul>
                <li>Health: 100</li>
                <li>Speed: Medium</li>
                <li>Starting Weapon: Crossbow</li>
                <li>Special: Long range attacks</li>
            </ul>
            <button class="select-character-btn" data-character="rogue">Select Rogue</button>
        `;
        
        // Add options to container
        characterOptions.appendChild(barbarianOption);
        characterOptions.appendChild(mageOption);
        characterOptions.appendChild(knightOption);
        characterOptions.appendChild(rogueOption);
        this.characterSelectScreen.appendChild(characterOptions);
        
        // Add to document
        document.getElementById('game-container').appendChild(this.characterSelectScreen);
        
        // Update screens object
        this.screens.characterSelect = this.characterSelectScreen;
        
        // Add event listeners to selection buttons
        const selectButtons = this.characterSelectScreen.querySelectorAll('.select-character-btn');
        selectButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                const characterType = e.target.getAttribute('data-character');
                this.selectedCharacterClass = characterType;
                
                if (this.callbacks.onCharacterSelect) {
                    this.callbacks.onCharacterSelect(characterType);
                }
                
                this.showScreen('game');
            });
        });
    }
    
    /**
     * Create in-game UI elements
     */
    createGameUI() {
        console.log('Creating game UI elements...');
        
        // Check for and remove existing game UI to prevent duplicates
        const existingUI = document.querySelector('.game-ui');
        if (existingUI) {
            console.log('Found existing game UI, removing it to prevent duplicates');
            existingUI.remove();
        }
        
        // Create game UI container
        this.gameUI = document.createElement('div');
        this.gameUI.className = 'game-ui';
        
        // Create health bar
        const healthBar = document.createElement('div');
        healthBar.className = 'health-bar hidden';
        healthBar.innerHTML = `
            <div class="bar-label">Health</div>
            <div class="bar-container">
                <div class="bar-fill health-fill" style="width: 100%"></div>
            </div>
            <div class="bar-value">100/100</div>
        `;
        this.gameUI.appendChild(healthBar);
        this.uiElements.healthBar = healthBar.querySelector('.health-fill');
        this.uiElements.healthValue = healthBar.querySelector('.bar-value');
        
        // Create XP bar
        const xpBar = document.createElement('div');
        xpBar.className = 'xp-bar hidden';
        xpBar.innerHTML = `
            <div class="bar-label">XP</div>
            <div class="bar-container">
                <div class="bar-fill xp-fill" style="width: 0%"></div>
            </div>
            <div class="bar-value">0/100</div>
        `;
        this.gameUI.appendChild(xpBar);
        this.uiElements.xpBar = xpBar.querySelector('.xp-fill');
        this.uiElements.xpValue = xpBar.querySelector('.bar-value');
        
        // Create Wave Info Display
        const waveInfo = document.createElement('div');
        waveInfo.className = 'wave-info hidden';
        waveInfo.innerHTML = `
            <div class="wave-header">Wave <span class="wave-number">1</span></div>
            <div class="wave-countdown hidden">Next wave in: <span class="countdown-value">0</span>s</div>
        `;
        this.gameUI.appendChild(waveInfo);
        this.uiElements.waveInfo = waveInfo;
        this.uiElements.waveNumber = waveInfo.querySelector('.wave-number');
        this.uiElements.waveCountdown = waveInfo.querySelector('.wave-countdown');
        this.uiElements.countdownValue = waveInfo.querySelector('.countdown-value');
        
        // Add game UI to document body
        document.body.appendChild(this.gameUI);
        
        console.log('Game UI created and added to DOM with elements:', Object.keys(this.uiElements));
        
        // Initial update to ensure elements are visible
        this.updateHealth(100, 100);
        this.updateXP(1, 0, 100);
        
        return this.gameUI;
    }
    
    /**
     * Show game UI elements after character selection
     */
    showGameUI() {
        const healthBar = this.gameUI.querySelector('.health-bar');
        const xpBar = this.gameUI.querySelector('.xp-bar');
        const waveInfo = this.gameUI.querySelector('.wave-info');
        
        if (healthBar) healthBar.classList.remove('hidden');
        if (xpBar) xpBar.classList.remove('hidden');
        if (waveInfo) waveInfo.classList.remove('hidden');
    }
    
    /**
     * Set up mobile-specific optimizations and adjustments
     */
    setupMobileOptimization() {
        // Detect if we're on mobile
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        if (isMobile) {
            document.body.classList.add('mobile');
            
            // // Adjust button sizes for touch targets
            // const buttons = document.querySelectorAll('button');
            // buttons.forEach(button => {
            //     button.classList.add('touch-target');
            // });
            
            // Add mobile controls for movement and aim
            this.createMobileControls();
            
            console.log('Mobile optimizations applied');
        }
    }
    
    /**
     * Create mobile-specific controls
     */
    createMobileControls() {
        // Create joystick container
        const joystickContainer = document.createElement('div');
        joystickContainer.className = 'joystick-container';
        
        // Create joystick elements
        const joystickOuter = document.createElement('div');
        joystickOuter.className = 'joystick-outer';
        
        const joystickInner = document.createElement('div');
        joystickInner.className = 'joystick-inner';
        
        joystickOuter.appendChild(joystickInner);
        joystickContainer.appendChild(joystickOuter);
        
        // Create attack button container
        const actionButtons = document.createElement('div');
        actionButtons.className = 'action-buttons';
        
        // Create attack button
        const attackButton = document.createElement('button');
        attackButton.className = 'attack-button';
        attackButton.textContent = 'Attack';
        
        // Add touch event listener for attack button
        attackButton.addEventListener('touchstart', (e) => {
            e.preventDefault(); // Prevent default touch behavior
            if (window.game && window.game.player) {
                window.game.player.attack();
            }
        });
        
        actionButtons.appendChild(attackButton);
        
        // Add to overlay
        this.overlay.appendChild(joystickContainer);
        this.overlay.appendChild(actionButtons);
        
        // Store references
        this.uiElements.joystickOuter = joystickOuter;
        this.uiElements.joystickInner = joystickInner;
        this.uiElements.attackButton = attackButton;
    }
    
    /**
     * Set event callbacks
     * @param {Object} callbacks - Callback functions
     */
    setCallbacks(callbacks) {
        if (callbacks.onLogin) {
            this.callbacks.onLogin = callbacks.onLogin;
        }
        
        if (callbacks.onCharacterSelect) {
            this.callbacks.onCharacterSelect = callbacks.onCharacterSelect;
        }
        
        if (callbacks.onInventoryChange) {
            this.callbacks.onInventoryChange = callbacks.onInventoryChange;
        }
    }
    
    /**
     * Switch to a different UI screen
     * @param {string} screenName - Name of the screen to switch to
     */
    showScreen(screenName) {
        if (!this.screens[screenName]) {
            console.error(`Screen not found: ${screenName}`);
            return false;
        }
        
        // Hide all screens
        Object.values(this.screens).forEach(screen => {
            if (screen) {
                screen.classList.add('hidden');
            }
        });
        
        // Show the requested screen
        this.screens[screenName].classList.remove('hidden');
        
        // Update active screen tracker
        this.activeScreen = screenName;
        console.log(`Switched to screen: ${screenName}`);
        
        // Special handling for game screen
        if (screenName === 'game') {
            // Show wave info if we're in the game
            if (this.uiElements.waveInfo) {
                this.uiElements.waveInfo.classList.remove('hidden');
                console.log('Showing wave info in game screen');
            }
        } else {
            // Hide wave info for non-game screens
            if (this.uiElements.waveInfo) {
                this.uiElements.waveInfo.classList.add('hidden');
                console.log('Hiding wave info for non-game screen');
            }
        }
        
        return true;
    }
    
    /**
     * Handle login form submission
     * @param {boolean} isGuest - Whether logging in as guest
     */
    handleLogin(isGuest) {
        let username;
        
        if (isGuest) {
            username = document.getElementById('guest-name').value.trim();
            if (!username) {
                alert('Please enter a name to play as guest');
                return;
            }
        } else {
            username = document.getElementById('username').value.trim();
            const password = document.getElementById('password').value;
            
            if (!username || !password) {
                alert('Please enter both username and password');
                return;
            }
        }
        
        if (this.callbacks.onLogin) {
            this.callbacks.onLogin(username, isGuest);
        }
        
        // Show character selection screen
        this.showScreen('characterSelect');
    }
    
    /**
     * Select an inventory slot
     * @param {number} slotIndex - Slot index to select (0-3)
     */
    selectInventorySlot(slotIndex) {
        // Remove active class from all slots
        this.uiElements.inventorySlots.forEach(slot => {
            slot.classList.remove('active');
        });
        
        // Add active class to selected slot
        this.uiElements.inventorySlots[slotIndex].classList.add('active');
        
        // Call callback
        if (this.callbacks.onInventoryChange) {
            this.callbacks.onInventoryChange(slotIndex);
        }
    }
    
    /**
     * Update player health display
     * @param {number} current - Current health
     * @param {number} max - Maximum health
     * @param {boolean} showDamageEffect - Whether to flash the health bar when taking damage
     */
    updateHealth(current, max, showDamageEffect = false) {
        console.log(`UIManager.updateHealth(${current}, ${max}, ${showDamageEffect})`);
        
        // Ensure health is not negative
        current = Math.max(0, current);
        
        // CRITICAL FIX: If UI elements aren't found, try to find them in the DOM
        if (!this.uiElements.healthBar || !this.uiElements.healthValue) {
            console.warn('Health bar elements not found in UI manager, attempting to find them in DOM');
            
            // Try to locate health bar elements in the DOM
            const healthBar = document.querySelector('.health-fill');
            const healthValue = document.querySelector('.bar-value');
            
            if (healthBar && healthValue) {
                console.log('Found health bar elements in DOM, storing references');
                this.uiElements.healthBar = healthBar;
                this.uiElements.healthValue = healthValue;
            } else {
                console.error('Health bar elements not found in DOM, recreating game UI');
                // If we still can't find them, the UI might not be created yet
                this.createGameUI();
            }
        }
        
        if (this.uiElements.healthBar && this.uiElements.healthValue) {
            // Calculate health percentage
            const percentage = Math.max(0, Math.min(100, (current / max) * 100));
            
            // Update health bar width
            this.uiElements.healthBar.style.width = `${percentage}%`;
            this.uiElements.healthValue.textContent = `${Math.floor(current)}/${max}`;
            
            console.log(`Health updated to ${percentage.toFixed(1)}% (${Math.floor(current)}/${max})`);
            
            // Change color based on health percentage
            if (percentage === 0) {
                // Special handling for death (0 health)
                this.uiElements.healthBar.style.backgroundColor = '#000000';
                this.uiElements.healthBar.classList.remove('pulse-animation');
                this.uiElements.healthValue.style.color = '#ff0000';
                this.uiElements.healthValue.style.fontWeight = 'bold';
            } else if (percentage < 25) {
                this.uiElements.healthBar.style.backgroundColor = '#ff0000';
                this.uiElements.healthBar.classList.add('pulse-animation');
            } else if (percentage < 50) {
                this.uiElements.healthBar.style.backgroundColor = '#ff9900';
                this.uiElements.healthBar.classList.remove('pulse-animation');
            } else {
                this.uiElements.healthBar.style.backgroundColor = '#00cc00';
                this.uiElements.healthBar.classList.remove('pulse-animation');
            }
            
            // Add damage effect if requested
            if (showDamageEffect) {
                // Flash the health bar container
                const container = this.uiElements.healthBar.parentElement;
                if (container) {
                    // Add flash effect
                    container.style.boxShadow = '0 0 8px #ff0000';
                    
                    // Remove effect after animation
                    setTimeout(() => {
                        container.style.boxShadow = 'none';
                    }, 500);
                }
                
                // Make the health text briefly larger
                this.uiElements.healthValue.style.fontSize = '110%';
                this.uiElements.healthValue.style.color = '#ff0000';
                
                // Reset text style after animation (but not if player is dead)
                if (current > 0) {
                    setTimeout(() => {
                        this.uiElements.healthValue.style.fontSize = '';
                        this.uiElements.healthValue.style.color = '';
                    }, 500);
                }
            }
        } else {
            console.error('Failed to update health: UI elements not available');
        }
    }
    
    /**
     * Update player XP display
     * @param {number} level - Current level
     * @param {number} xp - Current XP
     * @param {number} nextLevelXP - XP required for next level
     */
    updateXP(level, xp, nextLevelXP) {
        console.log(`UIManager.updateXP(${level}, ${xp}, ${nextLevelXP})`);
        
        // CRITICAL FIX: If UI elements aren't found, try to find them in the DOM
        if (!this.uiElements.xpBar || !this.uiElements.xpValue) {
            console.warn('XP bar elements not found in UI manager, attempting to find them in DOM');
            
            // Try to locate XP bar elements in the DOM
            const xpBar = document.querySelector('.xp-fill');
            const xpValue = document.querySelector('.xp-bar .bar-value');
            
            if (xpBar && xpValue) {
                console.log('Found XP bar elements in DOM, storing references');
                this.uiElements.xpBar = xpBar;
                this.uiElements.xpValue = xpValue;
            } else {
                console.error('XP bar elements not found in DOM, recreating game UI');
                // If we still can't find them, the UI might not be created yet
                this.createGameUI();
            }
        }
        
        if (this.uiElements.xpBar && this.uiElements.xpValue) {
            const percentage = (xp / nextLevelXP) * 100;
            this.uiElements.xpBar.style.width = `${percentage}%`;
            this.uiElements.xpValue.textContent = `Level ${level} (${xp}/${nextLevelXP})`;
            
            console.log(`XP updated to ${percentage.toFixed(1)}% - Level ${level} (${xp}/${nextLevelXP})`);
            
            // ENHANCEMENT: Add animation/highlight when XP increases
            this.uiElements.xpBar.style.backgroundColor = '#00aaff';
            setTimeout(() => {
                this.uiElements.xpBar.style.backgroundColor = '#4d7aff';
            }, 500);
        } else {
            console.error('Failed to update XP: UI elements not available');
        }
    }
    
    /**
     * Update the wave info display
     * @param {number} waveNumber - Current wave number
     * @param {number} enemiesRemaining - Number of enemies remaining in the wave (no longer displayed)
     */
    updateWaveInfo(waveNumber, enemiesRemaining) {
        // Ensure wave info element exists
        if (!this.uiElements.waveInfo) {
            console.warn('Wave info UI element not found');
            return;
        }
        
        // Update wave number
        if (this.uiElements.waveNumber) {
            this.uiElements.waveNumber.textContent = waveNumber;
        }
        
        // Only show wave info when in-game (not during loading or login)
        // - We check the active screen to determine if we're in-game
        if (this.activeScreen === 'game') {
            this.uiElements.waveInfo.classList.remove('hidden');
            console.log('Showing wave info in game screen');
        } else {
            this.uiElements.waveInfo.classList.add('hidden');
            console.log('Hiding wave info - not in game screen');
        }
    }
    
    /**
     * Update the wave countdown
     * @param {number} seconds - Seconds until next wave
     */
    showWaveCountdown(seconds) {
        // Clear any existing interval to prevent multiple countdowns
        if (this._countdownInterval) {
            clearInterval(this._countdownInterval);
            this._countdownInterval = null;
        }
        
        // Only show wave countdown if we're in the game screen
        if (this.activeScreen !== 'game') {
            console.log('Not showing wave countdown - not in game screen');
            return;
        }
        
        // Ensure wave info is visible (if we're in game)
        if (this.uiElements.waveInfo) {
            this.uiElements.waveInfo.classList.remove('hidden');
        }
        
        if (!this.uiElements.waveCountdown || !this.uiElements.countdownValue) {
            console.warn('Wave countdown UI elements not found');
            return;
        }
        
        // Show the countdown element
        this.uiElements.waveCountdown.classList.remove('hidden');
        
        // Update the countdown value
        this.uiElements.countdownValue.textContent = Math.max(0, Math.ceil(seconds));
        
        // If the countdown is zero, hide the element
        if (seconds <= 0) {
            this.uiElements.waveCountdown.classList.add('hidden');
            return;
        }
        
        // Add highlight effect to make countdown more visible
        this.uiElements.countdownValue.classList.add('highlight');
        
        // Create countdown animation
        this._countdownInterval = setInterval(() => {
            if (!this.uiElements.countdownValue) {
                clearInterval(this._countdownInterval);
                return;
            }
            
            // Get current value
            let value = parseInt(this.uiElements.countdownValue.textContent);
            
            // Decrement
            value--;
            
            // Update display
            if (value > 0) {
                this.uiElements.countdownValue.textContent = value;
                
                // Add pulse animation for last 3 seconds
                if (value <= 3) {
                    this.uiElements.countdownValue.classList.add('pulse-animation');
                }
            } else {
                this.uiElements.countdownValue.textContent = "0";
                this.uiElements.waveCountdown.classList.add('hidden');
                this.uiElements.countdownValue.classList.remove('pulse-animation', 'highlight');
                clearInterval(this._countdownInterval);
                this._countdownInterval = null;
            }
        }, 1000);
    }
    
    /**
     * Update the loading progress bar
     * @param {number} progress - Progress percentage (0-100)
     * @param {string} message - Optional message to display
     */
    updateLoadingProgress(progress, message) {
        // Update loading screen progress (if it exists)
        const loadingScreenProgress = document.querySelector('.loading-progress');
        const loadingScreenText = document.querySelector('.loading-text');
        
        if (loadingScreenProgress) {
            loadingScreenProgress.style.width = `${progress}%`;
        }
        
        if (loadingScreenText && message) {
            loadingScreenText.textContent = message;
        } else if (loadingScreenText) {
            loadingScreenText.textContent = `Loading... ${Math.round(progress)}%`;
        }
        
        // Also update login screen progress (our new approach)
        const loginProgress = document.querySelector('.login-loading-progress');
        const loginLoadingText = document.querySelector('.login-loading-text');
        
        if (loginProgress) {
            loginProgress.style.width = `${progress}%`;
        }
        
        if (loginLoadingText && message) {
            loginLoadingText.textContent = message;
        } else if (loginLoadingText) {
            loginLoadingText.textContent = `Loading game assets... ${Math.round(progress)}%`;
        }
    }
    
    /**
     * Create a UI element and add it to the overlay
     * @param {string} type - Type of element (e.g., 'div', 'button')
     * @param {string} id - Element ID
     * @param {string} className - CSS class name
     * @param {string} content - Inner HTML content
     * @param {Object} styles - CSS styles as object
     * @returns {HTMLElement} The created element
     */
    createElement(type, id, className, content, styles = {}) {
        const element = document.createElement(type);
        
        if (id) element.id = id;
        if (className) element.className = className;
        if (content) element.innerHTML = content;
        
        // Apply styles
        if (styles && typeof styles === 'object') {
            Object.assign(element.style, styles);
        }
        
        // Store reference to element
        this.uiElements[id] = element;
        
        return element;
    }
    
    /**
     * Add an element to the UI overlay
     * @param {HTMLElement} element - Element to add
     * @param {string} targetId - ID of target container (defaults to overlay)
     */
    addElement(element, targetId = null) {
        const target = targetId ? document.getElementById(targetId) : this.overlay;
        
        if (target) {
            target.appendChild(element);
        } else {
            console.error(`Target container not found: ${targetId}`);
        }
    }
    
    /**
     * Add event listener to an element
     * @param {string} elementId - ID of the element
     * @param {string} eventType - Type of event (e.g., 'click')
     * @param {Function} callback - Event handler function
     */
    addEventListenerToElement(elementId, eventType, callback) {
        const element = document.getElementById(elementId) || this.uiElements[elementId];
        
        if (element) {
            element.addEventListener(eventType, callback);
            
            // Store reference to listener for cleanup
            if (!this.eventListeners[elementId]) {
                this.eventListeners[elementId] = [];
            }
            
            this.eventListeners[elementId].push({
                type: eventType,
                callback: callback
            });
        } else {
            console.error(`Element not found for event listener: ${elementId}`);
        }
    }
    
    /**
     * Remove all event listeners
     */
    removeAllEventListeners() {
        for (const elementId in this.eventListeners) {
            const element = document.getElementById(elementId) || this.uiElements[elementId];
            
            if (element) {
                this.eventListeners[elementId].forEach(listener => {
                    element.removeEventListener(listener.type, listener.callback);
                });
            }
        }
        
        this.eventListeners = {};
    }
    
    /**
     * Clean up resources
     */
    dispose() {
        this.removeAllEventListeners();
        this.uiElements = {};
    }
    
    /**
     * Add CSS for the health bar pulse animation
     */
    addPulseAnimationStyle() {
        // Create a style element
        const style = document.createElement('style');
        
        // Define the pulse animation
        style.textContent = `
            @keyframes pulse {
                0% { opacity: 1; }
                50% { opacity: 0.6; }
                100% { opacity: 1; }
            }
            
            .pulse-animation {
                animation: pulse 1s infinite;
            }
        `;
        
        // Add to document head
        document.head.appendChild(style);
    }
    
    /**
     * Create inventory UI elements
     */
    createInventoryUI() {
        // Create inventory slots
        const inventory = document.createElement('div');
        inventory.className = 'inventory';
        
        // Create 4 inventory slots
        for (let i = 0; i < 4; i++) {
            const slot = document.createElement('div');
            slot.className = 'inventory-slot';
            slot.setAttribute('data-slot', i);
            
            // Add key hint
            const keyHint = document.createElement('div');
            keyHint.className = 'key-hint';
            keyHint.textContent = (i + 1).toString();
            slot.appendChild(keyHint);
            
            // Add event listener
            slot.addEventListener('click', (e) => {
                const slotIndex = parseInt(e.currentTarget.getAttribute('data-slot'));
                this.selectInventorySlot(slotIndex);
            });
            
            inventory.appendChild(slot);
        }
        
        this.gameUI.appendChild(inventory);
        this.uiElements.inventorySlots = inventory.querySelectorAll('.inventory-slot');
    }
    
    /**
     * Add a start wave button to the UI
     * @param {Function} clickHandler - Function to call when button is clicked
     */
    addStartWaveButton(clickHandler) {
        // Create button if it doesn't exist
        let startWaveButton = document.getElementById('start-wave-button');
        
        if (!startWaveButton) {
            startWaveButton = this.createElement('button', 'start-wave-button', 'start-wave-btn', 'Start Wave');
            document.body.appendChild(startWaveButton);
            
            // Add click handler
            startWaveButton.addEventListener('click', clickHandler);
        }
        
        // Ensure button is visible
        startWaveButton.style.display = 'block';
    }
    
    /**
     * Add debug button to UI
     */
    addDebugButton() {
        // Create debug button container if it doesn't exist
        let debugContainer = document.getElementById('debug-container');
        if (!debugContainer) {
            debugContainer = document.createElement('div');
            debugContainer.id = 'debug-container';
            debugContainer.style.position = 'fixed';
            debugContainer.style.bottom = '10px';
            debugContainer.style.right = '10px';
            debugContainer.style.zIndex = '1000';
            document.body.appendChild(debugContainer);
        }
        
        // Create damage test button
        const damageButton = document.createElement('button');
        damageButton.textContent = 'Test Enemy Damage';
        damageButton.style.padding = '8px 12px';
        damageButton.style.marginLeft = '5px';
        damageButton.style.backgroundColor = '#ff3300';
        damageButton.style.color = 'white';
        damageButton.style.border = 'none';
        damageButton.style.borderRadius = '4px';
        damageButton.style.cursor = 'pointer';
        
        // Add event listener for damage button
        damageButton.addEventListener('click', () => {
            if (window.game && typeof window.game.testDamageEnemy === 'function') {
                window.game.testDamageEnemy();
            } else {
                console.warn('Game or testDamageEnemy function not available');
            }
        });
        
        // Add toggle debug button
        const debugButton = document.createElement('button');
        debugButton.textContent = 'Toggle Debug';
        debugButton.style.padding = '8px 12px';
        debugButton.style.backgroundColor = '#3366ff';
        debugButton.style.color = 'white';
        debugButton.style.border = 'none';
        debugButton.style.borderRadius = '4px';
        debugButton.style.cursor = 'pointer';
        
        // Add event listener for debug button
        debugButton.addEventListener('click', () => {
            if (window.game && typeof window.game.toggleEnemyDebug === 'function') {
                window.game.toggleEnemyDebug();
            } else {
                console.warn('Game or toggleEnemyDebug function not available');
            }
        });
        
        // Add buttons to container
        debugContainer.appendChild(debugButton);
        debugContainer.appendChild(damageButton);
    }
    
    /**
     * Add styles for UI elements
     */
    addUIStyles() {
        // Create a style element if it doesn't exist
        let styleElement = document.getElementById('ui-manager-styles');
        
        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = 'ui-manager-styles';
            document.head.appendChild(styleElement);
        }
        
        // Add CSS rules for UI elements
        styleElement.textContent = `
            .game-ui {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                pointer-events: none;
                z-index: 1000;
            }
            
            .health-bar, .xp-bar {
                position: absolute;
                left: 20px;
                width: 250px;
                background-color: rgba(0, 0, 0, 0.5);
                border-radius: 5px;
                padding: 10px;
                color: white;
                font-family: Arial, sans-serif;
                pointer-events: none;
            }
            
            .health-bar {
                top: 20px;
            }
            
            .xp-bar {
                top: 80px;
            }
            
            .bar-label {
                font-weight: bold;
                margin-bottom: 5px;
            }
            
            .bar-container {
                height: 15px;
                background-color: rgba(0, 0, 0, 0.3);
                border-radius: 3px;
                overflow: hidden;
                position: relative;
            }
            
            .bar-fill {
                height: 100%;
                transition: width 0.3s ease, background-color 0.3s ease;
            }
            
            .health-fill {
                background-color: #00cc00;
            }
            
            .xp-fill {
                background-color: #4d7aff;
            }
            
            .bar-value {
                text-align: center;
                font-size: 12px;
                margin-top: 5px;
            }
            
            /* Animation for XP gain */
            @keyframes xp-float {
                0% { transform: translateY(0); opacity: 1; }
                100% { transform: translateY(-40px); opacity: 0; }
            }
            
            .game-notification {
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                background-color: rgba(0, 0, 0, 0.7);
                color: white;
                padding: 20px;
                border-radius: 5px;
                font-family: Arial, sans-serif;
                font-size: 24px;
                z-index: 2000;
                text-align: center;
                pointer-events: none;
            }
            
            .xp-gain {
                color: #4d7aff;
                font-size: 20px;
                font-weight: bold;
                text-shadow: 0 0 5px rgba(77, 122, 255, 0.5);
            }
            
            .fade-out {
                opacity: 0;
                transition: opacity 1s ease-out;
            }
            
            .wave-info {
                position: absolute;
                top: 20px;
                right: 20px;
                background-color: rgba(0, 0, 0, 0.7);
                border-radius: 5px;
                padding: 12px 15px;
                color: white;
                font-family: Arial, sans-serif;
                text-align: center;
                min-width: 150px;
                pointer-events: none;
                transition: opacity 0.3s ease;
                box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
            }
            
            .wave-header {
                font-size: 20px;
                font-weight: bold;
                margin-bottom: 5px;
                color: #ffcc00;
            }
            
            .wave-countdown {
                margin-top: 5px;
                font-style: italic;
                color: #ffcc00;
                transition: all 0.3s ease;
            }
            
            .countdown-value {
                font-weight: bold;
                margin-left: 3px;
            }
            
            .countdown-value.highlight {
                color: #ff9900;
                text-shadow: 0 0 5px rgba(255, 153, 0, 0.7);
            }
            
            .countdown-value.pulse-animation {
                color: #ff5500;
                font-size: 18px;
                text-shadow: 0 0 8px rgba(255, 85, 0, 0.9);
            }
            
            .hidden {
                display: none;
            }
        `;
        
        console.log('UI styles added to document');
    }
    
    /**
     * Update boss information in the UI
     * @param {string} bossName - Name of the boss
     * @param {number} health - Current health of the boss
     * @param {number} maxHealth - Maximum health of the boss
     * @param {boolean} show - Whether to show or hide the boss health bar
     */
    updateBossInfo(bossName, health, maxHealth, show = true) {
        // Get or create the boss health bar container
        let bossHealthBar = document.querySelector('.boss-health-bar');
        
        if (!bossHealthBar && show) {
            // Create boss health bar if it doesn't exist
            bossHealthBar = document.createElement('div');
            bossHealthBar.className = 'boss-health-bar';
            
            // Create boss name element
            const bossNameElement = document.createElement('div');
            bossNameElement.className = 'boss-name';
            bossHealthBar.appendChild(bossNameElement);
            
            // Create health bar container
            const healthBarContainer = document.createElement('div');
            healthBarContainer.className = 'bar-container';
            
            // Create health bar fill
            const healthBarFill = document.createElement('div');
            healthBarFill.className = 'bar-fill boss-health-fill';
            healthBarContainer.appendChild(healthBarFill);
            
            bossHealthBar.appendChild(healthBarContainer);
            
            // Add to game UI
            if (this.gameUI) {
                this.gameUI.appendChild(bossHealthBar);
            } else {
                document.body.appendChild(bossHealthBar);
            }
        }
        
        if (bossHealthBar) {
            if (show) {
                // Update boss name
                const bossNameElement = bossHealthBar.querySelector('.boss-name');
                if (bossNameElement) {
                    bossNameElement.textContent = bossName;
                }
                
                // Update health bar
                const healthBarFill = bossHealthBar.querySelector('.boss-health-fill');
                if (healthBarFill) {
                    const healthPercent = Math.max(0, Math.min(100, (health / maxHealth) * 100));
                    healthBarFill.style.width = `${healthPercent}%`;
                    
                    // Change color based on health percentage
                    if (healthPercent < 25) {
                        healthBarFill.style.backgroundColor = '#ff0000'; // Critical health
                    } else {
                        healthBarFill.style.backgroundColor = '#ff3333'; // Normal health
                    }
                }
                
                // Show the boss health bar
                bossHealthBar.style.display = 'block';
            } else {
                // Hide the boss health bar
                bossHealthBar.style.display = 'none';
            }
        }
    }
} 